import { useState, useEffect, useCallback } from 'react';
import type { ToothState, ToothCondition } from './OdontogramChart';

// ─── Constants ───

const GENERAL_FLAGS = [
  { code: 'ok', label: 'OK' },
  { code: 'missing', label: 'Missing' },
  { code: 'root_fragment', label: 'Root Fragment' },
  { code: 'implant', label: 'Implant' },
  { code: 'impacted', label: 'Impacted' },
] as const;

const CONDITION_TYPES = [
  { code: 'caries', label: 'Caries', color: '#dc2626' },
  { code: 'composite', label: 'Composite', color: '#3b82f6' },
  { code: 'amalgam', label: 'Amalgam', color: '#6b7280' },
  { code: 'recurrent_caries', label: 'Recurrent Caries', color: '#f97316' },
] as const;

const CROWN_OPTIONS = [
  { code: '', label: 'None' },
  { code: 'ceramic', label: 'Ceramic' },
  { code: 'pfm', label: 'PFM' },
  { code: 'gold', label: 'Gold' },
  { code: 'metal', label: 'Metal' },
] as const;

const SURFACES = ['occlusal', 'mesial', 'distal', 'buccal', 'lingual'] as const;
const SURFACE_LABELS = ['Occlusal', 'Mesial', 'Distal', 'Buccal', 'Lingual'] as const;

// ─── Helpers ───

function defaultToothState(): ToothState {
  return { flags: ['ok'], conditions: [], crown: null };
}

// ─── Props ───

interface ToothConditionsModalProps {
  open: boolean;
  toothNum: number;
  initialState: ToothState | undefined;
  onSave: (toothNum: number, state: ToothState) => void;
  onClear: (toothNum: number) => void;
  onClose: () => void;
  primary: string;
}

// ─── Component ───

export function ToothConditionsModal({
  open,
  toothNum,
  initialState,
  onSave,
  onClear,
  onClose,
  primary,
}: ToothConditionsModalProps) {
  const [flags, setFlags] = useState<string[]>(['ok']);
  const [conditions, setConditions] = useState<ToothCondition[]>([]);
  const [crown, setCrown] = useState<string | null>(null);

  // Reset when tooth changes
  useEffect(() => {
    if (!open) return;
    const s = initialState ?? defaultToothState();
    setFlags([...s.flags]);
    setConditions(s.conditions.map((c) => ({ ...c, surfaces: [...c.surfaces] })));
    setCrown(s.crown);
  }, [open, toothNum, initialState]);

  const toggleFlag = useCallback((code: string) => {
    setFlags((prev) => {
      if (code === 'ok') return ['ok'];
      const without = prev.filter((f) => f !== 'ok');
      if (without.includes(code)) {
        const result = without.filter((f) => f !== code);
        return result.length === 0 ? ['ok'] : result;
      }
      return [...without, code];
    });
  }, []);

  const toggleConditionSurface = useCallback((condCode: string, surface: string) => {
    setConditions((prev) => {
      const existing = prev.find((c) => c.code === condCode);
      if (existing) {
        const newSurfaces = existing.surfaces.includes(surface)
          ? existing.surfaces.filter((s) => s !== surface)
          : [...existing.surfaces, surface];
        if (newSurfaces.length === 0) {
          return prev.filter((c) => c.code !== condCode);
        }
        return prev.map((c) => (c.code === condCode ? { ...c, surfaces: newSurfaces } : c));
      }
      return [...prev, { code: condCode, surfaces: [surface] }];
    });
  }, []);

  const handleSave = useCallback(() => {
    onSave(toothNum, { flags, conditions, crown });
    onClose();
  }, [toothNum, flags, conditions, crown, onSave, onClose]);

  const handleClear = useCallback(() => {
    onClear(toothNum);
    onClose();
  }, [toothNum, onClear, onClose]);

  if (!open) return null;

  const checkboxEl = (checked: boolean, onClick: () => void) => (
    <div
      onClick={onClick}
      style={{
        width: 20,
        height: 20,
        borderRadius: 4,
        border: checked ? `2px solid ${primary}` : '1.5px solid #d1d5db',
        background: checked ? primary : '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 640,
          maxHeight: '85vh',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--sp-5)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg)',
                fontWeight: 'var(--font-weight-semi)',
                color: primary,
                textAlign: 'center',
              }}
            >
              Part Conditions
            </h3>
          </div>
          <div
            style={{
              height: 28,
              padding: '0 10px',
              borderRadius: 14,
              background: primary,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Part #{toothNum}
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 'var(--sp-5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {/* General flags — horizontal checkboxes */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            {GENERAL_FLAGS.map((f) => (
              <label
                key={f.code}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                  fontWeight: flags.includes(f.code) ? 600 : 400,
                }}
                onClick={() => toggleFlag(f.code)}
              >
                {checkboxEl(flags.includes(f.code), () => {})}
                {f.label}
              </label>
            ))}
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--border)' }} />

          {/* Conditions table — each row has label + 5 surface checkboxes */}
          <div>
            {/* Surface header row */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '140px repeat(5, 1fr)',
                gap: 4,
                marginBottom: 8,
                paddingLeft: 0,
              }}
            >
              <div />
              {SURFACE_LABELS.map((s) => (
                <span
                  key={s}
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    fontWeight: 'var(--font-weight-medium)',
                    textAlign: 'center',
                  }}
                >
                  {s}
                </span>
              ))}
            </div>

            {/* Condition rows */}
            {CONDITION_TYPES.map((cond) => {
              const existing = conditions.find((c) => c.code === cond.code);
              return (
                <div
                  key={cond.code}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '140px repeat(5, 1fr)',
                    gap: 4,
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: existing ? 600 : 400,
                      color: existing ? cond.color : 'var(--text)',
                    }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: cond.color,
                        opacity: 0.8,
                        flexShrink: 0,
                      }}
                    />
                    {cond.label}
                  </div>
                  {SURFACES.map((surf) => (
                    <div key={surf} style={{ display: 'flex', justifyContent: 'center' }}>
                      {checkboxEl(existing?.surfaces.includes(surf) ?? false, () =>
                        toggleConditionSurface(cond.code, surf),
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Crown select */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
                width: 60,
              }}
            >
              Crown
            </span>
            <select
              value={crown ?? ''}
              onChange={(e) => setCrown(e.target.value || null)}
              style={{
                height: 36,
                padding: '0 12px',
                fontSize: 'var(--font-size-sm)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                minWidth: 160,
              }}
            >
              {CROWN_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--border)',
            gap: 'var(--sp-3)',
          }}
        >
          <button
            onClick={onClose}
            style={{
              height: 40,
              padding: '0 24px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleClear}
            style={{
              height: 40,
              padding: '0 24px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
            }}
          >
            Clear Chart
          </button>
          <button
            onClick={handleSave}
            style={{
              height: 40,
              padding: '0 32px',
              borderRadius: 'var(--radius-lg)',
              border: 'none',
              background: primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: 'pointer',
            }}
          >
            Save Chart
          </button>
        </div>
      </div>
    </div>
  );
}
